import { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useTheme } from '../context/ThemeContext';
import { scrollToSection } from '../utils/scrollToSection';
import DftMark from './DftMark';

const footerLinks = {
  Services: [
    { label: 'Custom Software', href: '/#services' },
    { label: 'Web Applications', href: '/#services' },
    { label: 'Mobile Apps', href: '/#services' },
    { label: 'AI & ML', href: '/#services' },
    { label: 'E-Commerce', href: '/#services' },
  ],
  Solutions: [
    { label: 'Business Intelligence', href: '/#integrations' },
    { label: 'Data Analytics', href: '/#integrations' },
    { label: 'Chatbots & AI', href: '/#integrations' },
    { label: 'Web Hosting', href: '/#integrations' },
    { label: 'Integrations', href: '/#integrations' },
  ],
  Company: [
    { label: 'About Us', href: '/#why-us' },
    { label: 'Industries', href: '/#industries' },
    { label: 'Contact', href: '/#contact' },
  ],
};

const socials = [
  { label: 'in', aria: 'LinkedIn', color: '#0A66C2' },
  { label: '𝕏', aria: 'Twitter', color: null },
  { label: 'ig', aria: 'Instagram', color: '#E4405F' },
  { label: 'fb', aria: 'Facebook', color: '#1877F2' },
];

export default function Footer() {
  const { colors } = useTheme();
  const [hoveredSocial, setHoveredSocial] = useState(null);
  const [logoHovered, setLogoHovered] = useState(null);

  const premiumBlue = '#6366f1';

  // Section links: scroll in place on the home page, otherwise route there
  // without a full page reload.
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const goToSection = (id) => (e) => {
    e.preventDefault();
    if (pathname === '/') scrollToSection(id);
    else navigate(`/#${id}`);
  };

  const s = {
    footer: {
      background: colors.bg,
      borderTop: `1px solid ${colors.border}`,
      padding: '4rem 5vw 2rem',
    },
    grid: {
      display: 'grid',
      gridTemplateColumns: '2fr 1fr 1fr 1fr 1fr',
      gap: '3rem', marginBottom: '3rem',
    },
    brand: {},
    logo: {
      fontFamily: "'Inter', sans-serif", fontWeight: 800,
      fontSize: '1.8rem', letterSpacing: '3px', color: colors.text, marginBottom: '1rem',
    },
    logoAccent: { color: colors.text },
    brandDesc: {
      color: colors.muted, fontSize: '0.85rem', lineHeight: 1.75,
      fontWeight: 300, maxWidth: '260px',
    },
    col: {},
    colHeading: {
      fontSize: '0.75rem', letterSpacing: '4px', textTransform: 'uppercase',
      color: colors.text, marginBottom: '1.2rem', fontWeight: 900,
    },
    colList: { listStyle: 'none', display: 'flex', flexDirection: 'column', gap: '0.7rem' },
    colLink: { color: colors.muted, fontSize: '0.75rem', transition: 'color 0.2s', fontWeight: 300 },
    bottom: {
      borderTop: `1px solid ${colors.border}`, paddingTop: '2rem',
      display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '1rem',
    },
    copy: { fontSize: '0.78rem', color: colors.muted },
    socials: { display: 'flex', gap: '0.8rem' },
    socialLink: {
      width: '36px', height: '36px',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      fontSize: '0.82rem', transition: 'all 0.2s', cursor: 'pointer',
    },
  };

  return (
    <footer style={s.footer}>
      <div style={s.grid} className="footer-grid">
        <div style={s.brand}>
          <div style={{ ...s.logo, cursor: 'pointer', transition: 'color 0.3s ease', display: 'flex', alignItems: 'center', gap: '0.45em' }}>
            <DftMark height="1.05em" />
            <span>
              <span
                style={{ transition: 'color 0.3s ease', color: logoHovered === 'deck' ? premiumBlue : colors.text, cursor: 'pointer' }}
                onMouseEnter={() => setLogoHovered('deck')}
                onMouseLeave={() => setLogoHovered(null)}
              >
                Deck
              </span>
              {' '}
              <span
                style={{ transition: 'color 0.3s ease', color: logoHovered === 'for' ? premiumBlue : colors.text, cursor: 'pointer' }}
                onMouseEnter={() => setLogoHovered('for')}
                onMouseLeave={() => setLogoHovered(null)}
              >
                For
              </span>
              {' '}
              <span
                style={{
                  ...s.logoAccent,
                  transition: 'color 0.3s ease',
                  color: logoHovered === 'tech' ? premiumBlue : colors.text,
                  cursor: 'pointer',
                }}
                onMouseEnter={() => setLogoHovered('tech')}
                onMouseLeave={() => setLogoHovered(null)}
              >
                Tech
              </span>
            </span>
          </div>
          <p style={s.brandDesc}>
            Your trusted partner for custom software, AI solutions, and digital
            transformation.
          </p>
        </div>
        {Object.entries(footerLinks).map(([heading, links]) => (
          <div key={heading} style={s.col}>
            <h4 style={s.colHeading}>{heading}</h4>
            <ul style={s.colList}>
              {links.map(link => (
                <li key={link.label}>
                  {link.href.startsWith('/#') ? (
                    <a href={link.href} onClick={goToSection(link.href.slice(2))} style={s.colLink} className="footer-col-link">{link.label}</a>
                  ) : (
                    <Link to={link.href} style={s.colLink} className="footer-col-link">{link.label}</Link>
                  )}
                </li>
              ))}
            </ul>
          </div>
        ))}
        <div style={s.col}>
          <h4 style={s.colHeading}>Legal</h4>
          <ul style={s.colList}>
            <li>
              <Link to="/privacy" style={s.colLink} className="footer-col-link">Privacy Policy</Link>
            </li>
          </ul>
        </div>
      </div>

      <div style={s.bottom}>
        <p style={s.copy}>© 2026 Deck For Tech Private Limited. All rights reserved.</p>
        <div style={s.socials}>
          {socials.map(({ label, aria, color }) => {
            const getIcon = () => {
              switch(aria) {
                case 'LinkedIn':
                  return <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor" xmlns="http://www.w3.org/2000/svg"><path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.225 0z"/></svg>;
                case 'Twitter':
                  return <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor" xmlns="http://www.w3.org/2000/svg"><path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24h-6.6l-5.165-6.75-5.9 6.75H2.556l7.73-8.835L1.75 2.25h6.755l4.677 6.182 5.462-6.182zm-1.161 17.52h1.833L7.084 4.126H5.117z"/></svg>;
                case 'Instagram':
                  return <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor" xmlns="http://www.w3.org/2000/svg"><path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.057-1.645.069-4.849.069-3.204 0-3.584-.012-4.849-.069-3.259-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163C8.716 0 8.289.013 7.02.072 2.697.272.273 2.69.073 7.052.01 8.317 0 8.743 0 12s.010 3.683.072 4.948c.2 4.358 2.618 6.78 6.98 6.98C8.322 23.99 8.75 24 12 24s3.677-.01 4.947-.072c4.358-.2 6.78-2.619 6.98-6.98.061-1.266.072-1.690.072-4.948 0-3.257-.009-3.683-.072-4.948-.2-4.358-2.619-6.78-6.98-6.98C15.678.013 15.259 0 12 0zm0 5.838a6.162 6.162 0 100 12.324 6.162 6.162 0 000-12.324zM12 16a4 4 0 110-8 4 4 0 010 8zm4.965-10.322a1.44 1.44 0 110-2.88 1.44 1.44 0 010 2.88z"/></svg>;
                case 'Facebook':
                  return <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor" xmlns="http://www.w3.org/2000/svg"><path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385h-3.047v-3.47h3.047v-2.642c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953h-1.832c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385c1.9.427 3.91.66 5.98.66 6.627 0 12-5.373 12-12z"/></svg>;
                default:
                  return label;
              }
            };

            const getUrl = () => {
              switch(aria) {
                case 'LinkedIn': return 'https://www.linkedin.com/in/deckfortech';
                case 'Twitter': return 'https://x.com/deckfortech';
                case 'Instagram': return 'https://www.instagram.com/deckfortech/';
                case 'Facebook': return 'https://www.facebook.com/profile.php?id=61588325882370';
                default: return '#';
              }
            };

            const brandColor = color || colors.text;
            return (
              <a
                key={aria}
                href={getUrl()}
                aria-label={aria}
                target="_blank"
                rel="noopener noreferrer"
                style={{
                  ...s.socialLink,
                  color: hoveredSocial === aria ? brandColor : colors.text,
                  padding: '4px',
                  backgroundColor: 'transparent',
                  boxShadow: hoveredSocial === aria ? `0 0 12px ${brandColor}80` : 'none',
                  transform: hoveredSocial === aria ? 'scale(1.15)' : 'scale(1)',
                }}
                onMouseEnter={() => setHoveredSocial(aria)}
                onMouseLeave={() => setHoveredSocial(null)}
              >
                {getIcon()}
              </a>
            );
          })}
        </div>
      </div>

      <style>{`
        .footer-grid { grid-template-columns: 2fr 1fr 1fr 1fr 1fr; }
        @media (max-width: 900px) {
          .footer-grid { grid-template-columns: 1fr 1fr !important; }
        }
        @media (max-width: 500px) {
          .footer-grid { grid-template-columns: 1fr !important; }
        }
        .footer-col-link:hover { color: ${colors.text} !important; }
      `}</style>
    </footer>
  );
}
